import { createContext, useContext, useState, useCallback, ReactNode, FC } from 'react';
import { ModalContentProps } from '../../types';

interface ModalState {
  isOpen: boolean;
  // We store a function that returns React Node, allowing us to pass props
  content: ((props: ModalContentProps) => ReactNode) | null;
}

interface ModalContextType extends ModalState {
  openModal: (content: (props: ModalContentProps) => ReactNode) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: FC<ModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    content: null,
  });

  const openModal = useCallback((content: (props: ModalContentProps) => ReactNode) => {
    setModalState({ isOpen: true, content });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return (
    <ModalContext.Provider value={{ ...modalState, openModal, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
